import { InfoCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';

import * as Popover from './Popover';
import type { PopoverProps } from './Popover';
import { useDesignSystemTheme } from '../Hooks';

export interface InfoPopoverProps extends React.HTMLAttributes<HTMLButtonElement> {
  popoverProps?: Omit<PopoverProps, 'children' | 'title'>;
  iconProps?: React.HTMLAttributes<HTMLSpanElement>;
  iconTitle?: string;
  isKeyboardFocusable?: boolean;
  ariaLabel?: string;
}

export const InfoPopover = ({
  children,
  popoverProps,
  iconTitle,
  iconProps,
  isKeyboardFocusable = true,
  ariaLabel = 'More details',
}: InfoPopoverProps): JSX.Element => {
  const { theme } = useDesignSystemTheme();

  const [open, setOpen] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent<any>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(!open);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <span
          style={{ display: 'inline-flex', cursor: 'pointer' }}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={isKeyboardFocusable ? 0 : -1}
          onKeyDown={handleKeyDown}
          aria-label={ariaLabel}
        >
          <InfoCircleOutlined
            aria-hidden="false"
            title={iconTitle}
            aria-label={iconTitle}
            css={{
              fontSize: theme.typography.fontSizeSm,
              color: theme.colors.textSecondary,
            }}
            {...iconProps}
          />
        </span>
      </Popover.Trigger>
      <Popover.Content align="start" {...popoverProps}>
        {children}
        <Popover.Arrow />
      </Popover.Content>
    </Popover.Root>
  );
};
